import MotivationalLoading from 'components/common/MotivationalLoading';
import { TIMEOUT_DELAY } from 'constants/general';
import { MAIN_LAYOUT_EXCLUDED_ROUTES, ROUTES } from 'constants/routes';
import Layout from 'dashboard/layout';
import OnboardingWelcome from 'dashboard/onboard-flow/OnboardingWelcome';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { getTutorials } from 'store/reducer/course/extra';
import { getUserInstalledHabitPacks } from 'store/reducer/habit-pack/extra';
import {
  getLibraryActivities,
  getLocalDeviceSettings,
  getUserSettings
} from 'store/reducer/setting/extra';
import {
  getStripePlans,
  getSubscriptionInfo
} from 'store/reducer/subscription/extra';
import {
  getSyncedExternalPlatforms,
  getUserDetails,
  getUserFocusModes,
  getUserTimezone
} from 'store/reducer/user/extra';
import { joinTeamViaJoinCode } from 'store/reducer/team/extra';
import { isProduction, isUserInternalTest, runAfterDelay } from 'utils/helpers';
import { getStoredJoinCode } from 'utils/team-join.util';
import { isCurrentPath, isCurrentPathIn } from 'utils/routes.util';
import { protectedRoutesWrapperSelector } from 'store/reducer/selectors/navigation-routes.selector';
import { identifyPostHogAuthUser } from 'services/posthog.service';
import Drawer from 'components/ui/Drawer';
import { updateIsShowPrivacyPolicyModal } from 'store/reducer/setting/slice';
import PrivacySecurity from 'dashboard/PrivacySecurity';
import { t } from 'services/i18n.service';
import RefreshFab from 'components/ui/RefreshFab';

export const ProtectedRoutesWrapper = () => {
  const dispatch = useAppDispatch();
  const [quoteVisible, setQuoteVisible] = useState(true);

  const {
    userDetails,
    auth0User,
    isShowPrivacyPolicyModal,
    language,
    isFetchingDeviceSettings
  } = useAppSelectorShallow(protectedRoutesWrapperSelector);

  useEffect(() => {
    if (
      isProduction &&
      auth0User &&
      !isUserInternalTest(auth0User?.email) &&
      userDetails?.id
    ) {
      identifyPostHogAuthUser(auth0User, userDetails.id);
    }
  }, [userDetails?.id, auth0User]);

  useEffect(() => {
    const joinCode = getStoredJoinCode();
    if (userDetails?.id && joinCode) {
      dispatch(joinTeamViaJoinCode(joinCode));
    }
  }, [userDetails?.id, dispatch]);

  useEffect(() => {
    // Start sync to reduce user waiting time which helps to:
    // - Preload essential data in parallel
    // - Minimize loading states on subsequent page visits
    // - Improve perceived performance by fetching data early
    const promises = [
      dispatch(getLocalDeviceSettings()),
      dispatch(getSubscriptionInfo()),
      dispatch(getStripePlans()),
      dispatch(getUserDetails()).then(() => dispatch(getUserTimezone())),
      dispatch(getUserFocusModes()),
      dispatch(getUserSettings()),
      dispatch(getTutorials()),
      dispatch(getLibraryActivities()),
      dispatch(getUserInstalledHabitPacks(false)),
      dispatch(getSyncedExternalPlatforms())
    ];
    Promise.allSettled(promises);

    runAfterDelay(() => {
      setQuoteVisible(false);
    }, TIMEOUT_DELAY.MOTIVATIONAL_QUOTE);
  }, [dispatch]);

  if (quoteVisible || isFetchingDeviceSettings) {
    return isCurrentPath(ROUTES.ONBOARD_FLOW) ? (
      <OnboardingWelcome />
    ) : (
      <MotivationalLoading />
    );
  } else {
    return (
      <>
        {isCurrentPathIn(MAIN_LAYOUT_EXCLUDED_ROUTES) ? (
          <Outlet />
        ) : (
          <Layout key={language} />
        )}
        <Drawer
          isOpen={isShowPrivacyPolicyModal}
          onClose={() => dispatch(updateIsShowPrivacyPolicyModal(false))}
          side='bottom'
          height='auto'
          header={t('privacy_security.privacy_and_security')}
          ariaLabel={t('privacy_security.privacy_and_security')}
          className='max-h-[90vh]'>
          <PrivacySecurity
            isShowPrivacyPolicyModal={isShowPrivacyPolicyModal}
          />
        </Drawer>
        <RefreshFab onRefresh={() => window.location.reload()} />
      </>
    );
  }
};
export default ProtectedRoutesWrapper;
